import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { DateTime } from 'luxon';
import { AuthorizedEmail, newAuthorizedEmail } from '../models/authorizedEmails';
import { Campagne, newCampagne } from '../models/campagnes';
import { Franchise, newFranchise, PointDeService } from '../models/franchise';
import { GalleryComment, newGalleryComment } from '../models/galleryComment';
import { newProject, Project } from '../models/projects';
import { newTerritory, Territory } from '../models/territories';

export interface SubmissionFilter {
    accepted: boolean,
    enCours: boolean,
    refuse: boolean;
    mySubmissions: boolean;
}

export interface AdminState {
    newAuthorizedEmail: AuthorizedEmail;
    newFranchise: Franchise,
    newTerritory: Territory,
    newCampagne: Campagne,
    newProject: Project,
    newGalleryComment: GalleryComment;
    editGalleryComment?: GalleryComment;
    submissionFilter: SubmissionFilter;
    trackingFrom: number,
    trackingTo: number,
    newPointDeService: PointDeService;
}

const initialState: AdminState = {
    newAuthorizedEmail: { ...newAuthorizedEmail },
    newFranchise: { ...newFranchise },
    newTerritory: { ...newTerritory },
    newProject: { ...newProject },
    newCampagne: { ...newCampagne },
    newGalleryComment: { ...newGalleryComment },
    submissionFilter: {
        accepted: true,
        enCours: true,
        refuse: true,
        mySubmissions: false
    },
    trackingFrom: DateTime.now().set({ hour: 0, minute: 0, second: 0 }).toMillis(),
    trackingTo: DateTime.now().set({ hour: 23, minute: 59, second: 59 }).toMillis(),
    newPointDeService: {
        address: '',
        googlMap: '',
        title: '',
        tel: ''
    }

};

export const adminSlice = createSlice({
    name: 'admin',
    initialState,
    reducers: {
        clear(state) {
            state.newAuthorizedEmail = { ...newAuthorizedEmail };
            state.newFranchise = { ...newFranchise };
            state.newTerritory = { ...newTerritory };
            state.newProject = { ...newProject };
            state.newGalleryComment = { ...newGalleryComment };
        },
        updateNewAuthorizedEmail(state, action: PayloadAction<Partial<AuthorizedEmail>>) {
            state.newAuthorizedEmail = { ...state.newAuthorizedEmail, ...action.payload };
        },
        updateNewFranchise(state, action: PayloadAction<Partial<Franchise>>) {
            state.newFranchise = { ...state.newFranchise, ...action.payload };
        },
        updateNewCampagne(state, action: PayloadAction<Partial<Campagne>>) {
            state.newCampagne = { ...state.newCampagne, ...action.payload };
        },
        updateNewTerritory(state, action: PayloadAction<Partial<Territory>>) {
            state.newTerritory = { ...state.newTerritory, ...action.payload };
        },
        updateNewProject(state, action: PayloadAction<Partial<Project>>) {
            state.newProject = { ...state.newProject, ...action.payload };
        },
        updateNewGalleryComment(state, action: PayloadAction<GalleryComment>) {
            state.newGalleryComment = { ...state.newGalleryComment, ...action.payload };
        },
        updateEditGalleryComment(state, action: PayloadAction<GalleryComment | undefined>) {
            state.editGalleryComment = action.payload;
        },
        updateSubmissionFilter(state, action: PayloadAction<Partial<SubmissionFilter>>) {
            state.submissionFilter = { ...state.submissionFilter, ...action.payload };
        },
        setTrackingFilterFrom(state, action: PayloadAction<Date>) {
            state.trackingFrom = action.payload.valueOf();
        },
        setTrackingFilterTo(state, action: PayloadAction<Date>) {
            state.trackingTo = action.payload.valueOf();
        },
        updateNewPointDeService(state, action: PayloadAction<Partial<PointDeService>>) {
            state.newPointDeService = { ...state.newPointDeService, ...action.payload };
        }
    }
});
